import { Check, ChevronRight, Compass, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../ds";
import type { GuideStep, GuideStepId } from "./guide";

/** Top-bar "Guide" button + checklist popover: the 5 steps from an empty report to a published one. */
export function GuideButton({ steps, onAction }: { steps: GuideStep[]; onAction: (id: GuideStepId) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", h);
    window.addEventListener("keydown", k);
    return () => { window.removeEventListener("mousedown", h); window.removeEventListener("keydown", k); };
  }, [open]);
  return (
    <span className="st-guide-wrap" ref={ref}>
      <Button icon={Compass} aria-expanded={open} className={`st-guide-btn${next && done < 2 ? " is-nudge" : ""}`} title="Step-by-step help for building this dashboard" onClick={() => setOpen((o) => !o)}>
        Guide <span className="st-guide-count">{done}/{steps.length}</span>
      </Button>
      {open ? (
        <div className="st-guide" role="dialog" aria-label="Guide: build a dashboard in 5 steps">
          <div className="st-guide__head">
            <strong>{next ? "Build your dashboard in 5 steps" : "All done - nice work"}</strong>
            <button type="button" className="st-mini" aria-label="Close guide" onClick={() => setOpen(false)}><X size={13} /></button>
          </div>
          <ol className="st-guide__list">
            {steps.map((s, i) => (
              <li key={s.id} className={`st-guide__step${s.done ? " is-done" : ""}${next?.id === s.id ? " is-next" : ""}`}>
                <span className="st-guide__num">{s.done ? <Check size={13} /> : i + 1}</span>
                <div className="ds-grow">
                  <div className="st-guide__title">{s.title}</div>
                  <div className="ds-small ds-muted">{s.help}</div>
                </div>
                <Button size="sm" variant={next?.id === s.id ? "primary" : "ghost"} iconRight={ChevronRight} onClick={() => { setOpen(false); onAction(s.id); }}>
                  {s.done ? "Open" : "Do this"}
                </Button>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </span>
  );
}
